import React, { useEffect, useMemo, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import SimpleLoader from "../Loader/SimpleLoader";
import {
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { pdfjsLib, renderPageToDataUrl } from "./pdfUtils";

// Final render scale of booklet pages (helper supersamples at 2x this)
const RENDER_SCALE = 1.6;
// Vertical room the controls row needs below the book in fullscreen
const CONTROLS_HEIGHT = 96;

const isNativeFullscreen = () =>
  !!(document.fullscreenElement || document.webkitFullscreenElement);

const PdfFlipBook = ({ url, title }) => {
  const [pages, setPages] = useState([]);
  const [pageRatio, setPageRatio] = useState(1.41); // height / width
  const [progress, setProgress] = useState({ phase: "download", pct: 0 });
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Force two-page spread on wide screens; the library's own portrait
  // auto-detection is unreliable, so we decide explicitly.
  const [isWide, setIsWide] = useState(window.innerWidth >= 768);
  const [viewport, setViewport] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });
  const bookRef = useRef(null);
  const containerRef = useRef(null);
  // Mirrors currentPage for the listeners below, which would otherwise close
  // over a stale value.
  const currentPageRef = useRef(0);
  // Page to return to after the flipbook remounts (see the restore effect).
  const restorePageRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      setIsWide(window.innerWidth >= 768);
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const enterFullscreen = () => {
    restorePageRef.current = currentPageRef.current;
    setIsFullscreen(true);
    const el = containerRef.current;
    const request = el?.requestFullscreen || el?.webkitRequestFullscreen;
    // iOS Safari has no element fullscreen and rejects (or is absent) — the
    // CSS overlay alone already fills the screen there.
    request?.call(el)?.catch?.(() => {});
  };

  const exitFullscreen = () => {
    restorePageRef.current = currentPageRef.current;
    setIsFullscreen(false);
    if (isNativeFullscreen()) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      exit?.call(document)?.catch?.(() => {});
    }
  };

  // The user can leave native fullscreen on their own (Esc, F11, browser UI).
  // Without this the overlay would stay stuck on screen. Only the exit
  // direction is synced — on iOS no such event ever fires.
  useEffect(() => {
    const onChange = () => {
      if (!isNativeFullscreen()) {
        restorePageRef.current = currentPageRef.current;
        setIsFullscreen(false);
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        // Native fullscreen handles Esc itself; this covers the CSS-overlay
        // fallback, where nothing else would close the reader.
        exitFullscreen();
      } else if (e.key === "ArrowRight") {
        bookRef.current?.pageFlip()?.flipNext();
      } else if (e.key === "ArrowLeft") {
        bookRef.current?.pageFlip()?.flipPrev();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      // Unmounting while still fullscreen (browser back from the book) would
      // otherwise leave the browser fullscreen on an empty page.
      if (isNativeFullscreen()) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        exit?.call(document)?.catch?.(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  // Entering/leaving fullscreen and the orientation switch both remount the
  // flipbook, which drops it back to the cover. Put the reader back where
  // they were instead of restarting the story.
  useEffect(() => {
    const target = restorePageRef.current ?? 0;
    restorePageRef.current = null;
    const id = requestAnimationFrame(() => {
      if (target > 0) bookRef.current?.pageFlip()?.turnToPage(target);
      currentPageRef.current = target;
      setCurrentPage(target);
    });
    return () => cancelAnimationFrame(id);
  }, [isFullscreen, isWide]);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;

    const load = async () => {
      try {
        loadingTask = pdfjsLib.getDocument(url);
        loadingTask.onProgress = ({ loaded, total }) => {
          if (!cancelled && total) {
            setProgress({
              phase: "download",
              pct: Math.round((loaded / total) * 100),
            });
          }
        };
        const pdfDoc = await loadingTask.promise;
        if (cancelled) return;

        // Long documents: drop the render scale so the up-front rasterize
        // pass stays fast and the page data URLs fit in memory.
        const scale = pdfDoc.numPages > 30 ? 1.0 : RENDER_SCALE;
        const rendered = [];
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          const page = await pdfDoc.getPage(i);
          const { dataUrl, width, height } = await renderPageToDataUrl(page, {
            scale,
          });
          if (i === 1) {
            setPageRatio(height / width);
          }
          rendered.push(dataUrl);
          if (cancelled) return;
          setProgress({
            phase: "render",
            pct: Math.round((i / pdfDoc.numPages) * 100),
          });
        }
        if (!cancelled) setPages(rendered);
      } catch (err) {
        console.error("PDF load error:", err);
        if (!cancelled) setError(true);
      }
    };

    load();
    return () => {
      cancelled = true;
      // destroy() on the loading task cancels an in-flight download and
      // tears down the document/worker — destroying only the resolved doc
      // would leak both when unmounting mid-download.
      loadingTask?.destroy();
    };
  }, [url]);

  // Inline the book is a fixed size; fullscreen it is fitted to the viewport,
  // keeping the page's aspect ratio. A spread is two pages wide.
  const { pageWidth, pageHeight } = useMemo(() => {
    if (!isFullscreen) {
      return { pageWidth: 420, pageHeight: Math.round(420 * pageRatio) };
    }
    const availableHeight = viewport.h - CONTROLS_HEIGHT;
    const availableWidth = (viewport.w - 32) / (isWide ? 2 : 1);
    const height = Math.min(availableHeight, availableWidth * pageRatio);
    return {
      pageHeight: Math.round(height),
      pageWidth: Math.round(height / pageRatio),
    };
  }, [isFullscreen, isWide, viewport, pageRatio]);

  if (error) {
    return (
      <div className="text-center text-gray-600 py-16">
        Кітапшаны жүктеу мүмкін болмады.
      </div>
    );
  }

  if (!pages.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <SimpleLoader className="w-10 h-10 text-[#674DA2] mb-4" />
        <p className="text-[#674DA2] font-medium mb-2">
          {progress.phase === "download"
            ? "Кітапша жүктелуде..."
            : "Беттер дайындалуда..."}
        </p>
        <div className="w-64 bg-gray-200 rounded-full h-2">
          <div
            className="bg-[#674DA2] h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress.pct}%` }}
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">{progress.pct}%</p>
      </div>
    );
  }

  const controlButton = isFullscreen
    ? "bg-white/10 text-white hover:bg-white/20"
    : "bg-[#674DA2] text-white hover:bg-[#5a4291]";

  return (
    <div
      ref={containerRef}
      className={
        isFullscreen
          ? "fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center px-4"
          : "flex flex-col items-center"
      }
    >
      {isFullscreen && (
        <button
          onClick={exitFullscreen}
          className="absolute top-4 right-4 z-10 bg-white/10 text-white p-2.5 rounded-full hover:bg-white/20 transition"
          aria-label="Шығу"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      )}

      {/* Full-width wrapper so size="stretch" measures the real container
          width — inside items-center it collapses and forces portrait mode */}
      <div className="w-full">
        {/* Fullscreen size is already an exact fit, so min = max is pinned
            below: a computed width under the inline minWidth of 280 would
            otherwise invert the bounds and break the layout. */}
        <HTMLFlipBook
          key={`${isWide ? "landscape" : "portrait"}-${
            isFullscreen ? "fs" : "inline"
          }`}
          ref={bookRef}
          width={pageWidth}
          height={pageHeight}
          size="stretch"
          minWidth={isFullscreen ? pageWidth : 280}
          maxWidth={isFullscreen ? pageWidth : 550}
          minHeight={isFullscreen ? pageHeight : Math.round(280 * pageRatio)}
          maxHeight={isFullscreen ? pageHeight : Math.round(550 * pageRatio)}
          showCover={true}
          usePortrait={!isWide}
          mobileScrollSupport={true}
          maxShadowOpacity={0.4}
          className="shadow-2xl"
          style={{ margin: "0 auto" }}
          onFlip={(e) => {
            currentPageRef.current = e.data;
            setCurrentPage(e.data);
          }}
        >
          {pages.map((src, i) => (
            <div key={i} className="bg-white">
              <img
                src={src}
                alt={`${title || "Ертегі"} — ${i + 1}-бет`}
                className="w-full h-full object-contain select-none"
                draggable={false}
              />
            </div>
          ))}
        </HTMLFlipBook>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-6 mt-6">
        <button
          onClick={() => bookRef.current?.pageFlip()?.flipPrev()}
          disabled={currentPage === 0}
          className={`${controlButton} p-2.5 rounded-full transition disabled:opacity-40 disabled:cursor-default`}
          aria-label="Алдыңғы бет"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <span
          className={`${
            isFullscreen ? "text-white" : "text-[#674DA2]"
          } font-medium min-w-[80px] text-center`}
        >
          {currentPage + 1} / {pages.length}
        </span>
        <button
          onClick={() => bookRef.current?.pageFlip()?.flipNext()}
          disabled={currentPage >= pages.length - 1}
          className={`${controlButton} p-2.5 rounded-full transition disabled:opacity-40 disabled:cursor-default`}
          aria-label="Келесі бет"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
        {!isFullscreen && (
          <button
            onClick={enterFullscreen}
            className="bg-[#674DA2] text-white p-2.5 rounded-full hover:bg-[#5a4291] transition"
            aria-label="Толық экран"
            title="Толық экран"
          >
            <ArrowsPointingOutIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PdfFlipBook;
